import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import Swal from "sweetalert2";
import moment from "moment";
import "moment/locale/es";

moment.locale("es");

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    width: "30%",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    padding: "20px",
    borderRadius: "10px",
    border: "1px solid #ccc",
    boxShadow: "0 5px 15px rgba(0, 0, 0, 0.3)",
    fontFamily: "Montserrat, sans-serif",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 101,
  },
};

export default function ModalCreateEvent({
  isOpen,
  onClose,
  onSave,
  initialData,
  categories = [],
  onAddCategory,
}) {
  // Título, Fecha, Categoría, Color
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Eventos");
  const [newCategoryName, setNewCategoryName] = useState("");
  const [color, setColor] = useState("#007bff");
  const [hoverCancel, setHoverCancel] = useState(false);
  const [hoverSave, setHoverSave] = useState(false);

  // Paleta de colores
  const taskColorOptions = [
    { hex: "#e0e7ff" },
    { hex: "#ede9fe" },
    { hex: "#bae6fd" },
    { hex: "#a7f3d0" },
    { hex: "#fecaca" },
    { hex: "#fef9c3" },
    { hex: "#f0f0f0" },
    { hex: "#ddd" },
  ];

  // Cuando abre el modal para editar, llenamos los campos
  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title || "");
      setDate(moment(initialData.start).format("YYYY-MM-DDTHH:mm") || "");
      // Si es un "evento", la category la consideramos "Eventos"
      // Si es "tarea" => la que venga en initialData.category
      if (initialData.resource === "evento") {
        setSelectedCategory("Eventos");
      } else if (initialData.resource === "tarea") {
        setSelectedCategory(initialData.category || "");
      }
      setColor(initialData.color || "#007bff");
    } else {
      // Nuevo
      setTitle("");
      setDate("");
      setSelectedCategory("Eventos");
      setColor("#007bff");
      setNewCategoryName("");
    }
  }, [initialData]);

  // Manejo del scroll body
  useEffect(() => {
    if (isOpen) {
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      if (scrollbarWidth > 0) {
        document.body.style.overflow = "hidden";
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      } else {
        document.body.style.overflow = "hidden";
        document.body.style.paddingRight = "0";
      }
    } else {
      document.body.style.overflow = "auto";
      document.body.style.paddingRight = "0";
    }

    return () => {
      document.body.style.overflow = "auto";
      document.body.style.paddingRight = "0";
    };
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!title.trim() || !date.trim()) {
      Swal.fire("Error", "Título y Fecha son obligatorios.", "error");
      return;
    }
    let finalCategory = selectedCategory;
    // Si el usuario eligió "new", validamos la nueva cat
    if (selectedCategory === "new") {
      if (!newCategoryName.trim()) {
        Swal.fire("Error", "La nueva categoría no puede estar vacía.", "error");
        return;
      }
      // Creamos la categoría
      if (onAddCategory) {
        await onAddCategory(newCategoryName, color);
      }
      finalCategory = newCategoryName;
    }

    if (typeof onSave === "function") {
      await onSave({
        id: initialData?.id || null,
        title,
        date,
        category: finalCategory,
        color,
      });
    } else {
      Swal.fire("Error", "No se pudo guardar el evento.", "error");
    }
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={customStyles}
      contentLabel={initialData ? "Editar Evento" : "Agregar Evento"}
    >
      <button onClick={onClose} className="modal-close">
        <i className="bi bi-x-lg"></i>
      </button>
      <h2 style={{ textAlign: "center", marginBottom: "20px" }}>
        {initialData ? "Editar Evento" : "Agregar Evento"}
      </h2>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <div style={{ marginBottom: "15px" }}>
          <label style={{ display: "block", marginBottom: "5px" }}>
            Título:
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={{
              width: "100%",
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              boxSizing: "border-box",
            }}
            required
          />
        </div>
        <div style={{ marginBottom: "15px" }}>
          <label style={{ display: "block", marginBottom: "5px" }}>
            Fecha del evento:
          </label>
          <input
            type="datetime-local"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={{
              width: "100%",
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              boxSizing: "border-box",
            }}
            required
          />
        </div>
        <div style={{ marginBottom: "15px" }}>
          <label style={{ display: "block", marginBottom: "5px" }}>
            Categoría:
          </label>
          <select
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value);
              if (e.target.value !== "new") {
                setNewCategoryName("");
              }
            }}
            style={{
              width: "100%",
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              boxSizing: "border-box",
            }}
          >
            {/* "Eventos" es la palabra clave para guardarlo en la colec "eventos" */}
            <option value="Eventos">Eventos</option>

            {/* Pintamos las categorías que vengan de la DB:
                OJO: no pongas "Eventos" repetido si ya lo usas arriba */}
            {categories &&
              categories.length > 0 &&
              categories.map((cat) => {
                // Evitar la palabra "Eventos" duplicada
                if (cat.label === "Eventos") return null;
                return (
                  <option key={cat.id} value={cat.label}>
                    {cat.label}
                  </option>
                );
              })}

            <option value="new">+ Nueva Categoría</option>
          </select>
        </div>

        {/* Si el usuario eligió "new" => meter input nombre cat y paleta de color */}
        {selectedCategory === "new" && (
          <>
            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "block", marginBottom: "5px" }}>
                Nueva Categoría:
              </label>
              <input
                type="text"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="Nombre de la nueva categoría"
                style={{
                  width: "100%",
                  padding: "8px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                  boxSizing: "border-box",
                  marginBottom: "10px",
                }}
              />
            </div>
            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "block", marginBottom: "8px" }}>
                Color de la nueva categoría:
              </label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                {taskColorOptions.map((option, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setColor(option.hex)}
                    style={{
                      width: "35px",
                      height: "35px",
                      borderRadius: "50%",
                      border:
                        color === option.hex
                          ? "2px solid black"
                          : "2px solid transparent",
                      backgroundColor: option.hex,
                      cursor: "pointer",
                    }}
                  />
                ))}
              </div>
            </div>
          </>
        )}

        {/* Si la categoría es una ya existente (y != "Eventos"), dejamos cambiar color si gustas */}
        {selectedCategory !== "new" && selectedCategory !== "Eventos" && (
          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "8px" }}>
              Color:
            </label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
              {taskColorOptions.map((option, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setColor(option.hex)}
                  style={{
                    width: "35px",
                    height: "35px",
                    borderRadius: "50%",
                    border:
                      color === option.hex
                        ? "2px solid black"
                        : "2px solid transparent",
                    backgroundColor: option.hex,
                    cursor: "pointer",
                  }}
                />
              ))}
            </div>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "center" }}>
          <button
            type="submit"
            onMouseEnter={() => setHoverSave(true)}
            onMouseLeave={() => setHoverSave(false)}
            style={{
              padding: "10px 20px",
              margin: "0 5px",
              borderRadius: "4px",
              border: "none",
              backgroundColor: hoverSave ? "#0056b3" : "#007bff",
              color: "white",
              fontSize: "14px",
              cursor: "pointer",
              transition: "background-color 0.3s ease",
            }}
          >
            {initialData ? "Guardar Cambios" : "Agregar"}
          </button>
          <button
            type="button"
            onClick={onClose}
            onMouseEnter={() => setHoverCancel(true)}
            onMouseLeave={() => setHoverCancel(false)}
            style={{
              padding: "10px 20px",
              margin: "0 5px",
              borderRadius: "5px",
              border: "none",
              backgroundColor: hoverCancel ? "#999" : "#808080",
              color: "white",
              fontSize: "14px",
              cursor: "pointer",
              transition: "background-color 0.3s ease",
            }}
          >
            Cancelar
          </button>
        </div>
      </form>
    </Modal>
  );
}
